(ns io.daskul.tutorial.webapp.views.session-edit
  "Custom Session create/edit form for the calendar slide-over.

   Replaces the framework's auto-generated form with a wall-clock model:

     [Date]   [Start time]   [Duration ▾]

   Date is pre-filled to the calendar's current view date in create-mode.
   Duration defaults to the Program's default length and stays editable; the
   field shows the resulting end time and flags a session that runs longer than
   the Program's default. Submits date/start-time/end-time directly (#590) —
   end time is start + duration. Timezone is intentionally hidden — the server
   snapshots it from the active school's org-unit-config
   (`:tutorial.default/active-school-timezone`)."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $ use-effect use-state use-callback]]
   [uix.re-frame :as urf]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]
   [io.daskul.webapp.ui :as ui]
   ["@mantine/dates" :refer [DateInput TimeInput]]))

;; ============================================================================
;; Wall-clock helpers — the form works in date + "HH:MM" + duration, and the
;; API stores those verbatim (#590: sessions are naive local, no instants).
;; ============================================================================

(defn- two-digit [n]
  (if (< n 10) (str "0" n) (str n)))

(defn- date->ymd
  "js/Date -> 'YYYY-MM-DD' from local calendar components."
  [^js d]
  (str (.getFullYear d) "-" (two-digit (inc (.getMonth d))) "-" (two-digit (.getDate d))))

(defn- ymd->date
  "'YYYY-MM-DD' -> js/Date at local midnight (what DateInput expects)."
  [ymd]
  (let [[y m d] (mapv #(js/parseInt % 10) (str/split ymd #"-"))]
    (js/Date. y (dec m) d)))

(defn- hhmm
  "Trim a wall-clock time to 'HH:MM' for TimeInput — the API serves 'HH:MM:SS'."
  [t]
  (when t (subs t 0 5)))

(defn- time->minutes [t]
  (let [[h m] (str/split t #":")]
    (+ (* 60 (js/parseInt h 10)) (js/parseInt m 10))))

(defn- add-minutes
  "'HH:MM' + whole minutes -> 'HH:MM' (same day)."
  [t minutes]
  (let [total (+ (time->minutes t) minutes)]
    (str (two-digit (quot total 60)) ":" (two-digit (mod total 60)))))

;; ============================================================================
;; Lookup fetches — direct ajax (no caching, refetches per mount)
;; ============================================================================

(defn- fetch-list! [resource params on-success]
  (api/request!
   {:method :get
    :path (str "/" resource)
    :params (merge {:page-size 100} params)
    :on-success (fn [resp] (on-success (or (:items resp) [])))
    :on-error (fn [_] (on-success []))}))

(defn- ->options
  "Map [{:id ... :name ...}] -> Mantine Select data shape."
  [items label-fn]
  (mapv (fn [it] {:value (:id it) :label (label-fn it)}) items))

;; ============================================================================
;; Form
;; ============================================================================

(defui session-form
  "Props:
   - :mode         — :create or :edit
   - :session-id   — UUID string (required when :edit)
   - :default-date — js/Date for create-mode pre-fill (defaults to today)
   - :on-saved     — () → called after a successful save
   - :on-cancel    — () → called when user dismisses"
  [{:keys [mode session-id default-date on-saved on-cancel]}]
  (let [active-branch-id (urf/use-subscribe [::ac.subs/branch-id])
        ;; --- form state ---
        [branch-id set-branch-id!] (use-state active-branch-id)
        [program-id set-program-id!] (use-state nil)
        [teacher-id set-teacher-id!] (use-state nil)
        [date set-date!] (use-state (or default-date (js/Date.)))
        [start-time set-start-time!] (use-state "09:00")
        [duration-min set-duration-min!] (use-state 60)
        ;; Once the admin edits Duration we stop auto-deriving it from the
        ;; Program default — they've set an explicit length.
        [duration-touched? set-duration-touched!] (use-state false)
        [capacity set-capacity!] (use-state nil)
        [saving? set-saving!] (use-state false)
        [error set-error!] (use-state nil)
        ;; --- lookup options ---
        [branches set-branches!] (use-state [])
        [programs set-programs!] (use-state [])   ;; raw records (need :default-duration-minutes)
        [teachers set-teachers!] (use-state [])
        ;; --- loading existing for edit ---
        [loading? set-loading!] (use-state (= mode :edit))

        ;; Selected Program's default session length (minutes); 60 fallback.
        program-default-min (or (some->> programs
                                         (filter #(= program-id (:id %)))
                                         first
                                         :default-duration-minutes)
                                60)

        ;; --- Fetch lookups once on mount ---
        _ (use-effect
           (fn []
             (fetch-list! "org-units" {}
                          (fn [items]
                            (set-branches!
                             (->options (filter #(= "branch" (:type %)) items)
                                        :name))))
             ;; Keep the raw program records — need :default-duration-minutes,
             ;; not just id/name for the Select.
             (fetch-list! "programs" {} set-programs!)
             (fetch-list! "teachers" (when active-branch-id {:branch-id active-branch-id})
                          (fn [items]
                            (set-teachers!
                             (->options items
                                        (fn [t]
                                          (or (get-in t [:person :full-name])
                                              (str (get-in t [:person :first-name]) " "
                                                   (get-in t [:person :last-name]))
                                              "(unnamed)"))))))
             js/undefined)
           [active-branch-id])

        ;; --- Fetch existing session for edit mode ---
        _ (use-effect
           (fn []
             (when (and (= mode :edit) session-id)
               (api/request!
                {:method :get
                 :path (str "/sessions/" session-id)
                 :on-success
                 (fn [s]
                   (set-branch-id! (:branch-id s))
                   (set-program-id! (:program-id s))
                   (set-teacher-id! (:teacher-id s))
                   (when (:date s) (set-date! (ymd->date (:date s))))
                   (when (:start-time s) (set-start-time! (hhmm (:start-time s))))
                   (when (and (:start-time s) (:end-time s))
                     (set-duration-min! (- (time->minutes (:end-time s))
                                           (time->minutes (:start-time s))))
                     ;; An existing session already has an explicit length —
                     ;; never overwrite it from the Program default.
                     (set-duration-touched! true))
                   (set-capacity! (:capacity s))
                   (set-loading! false))
                 :on-error
                 (fn [_]
                   (set-error! "Failed to load session")
                   (set-loading! false))}))
             js/undefined)
           [mode session-id])

        ;; --- Default Duration from the Program default, until touched ---
        _ (use-effect
           (fn []
             (when-not duration-touched?
               (set-duration-min! program-default-min))
             js/undefined)
           [program-default-min duration-touched?])

        ;; Live flags + the computed end shown as an "Ends …" hint.
        invalid? (and (number? duration-min) (<= duration-min 0))
        over-default? (and (number? duration-min) (> duration-min program-default-min))
        end-hint (when (and start-time (number? duration-min) (pos? duration-min))
                   (add-minutes start-time duration-min))
        program-name (some->> programs (filter #(= program-id (:id %))) first :name)

        ;; --- Save ---
        on-save
        (use-callback
         (fn [e]
           (when e (.preventDefault e))
           (let [payload (cond-> {:branch-id  branch-id
                                  :program-id program-id
                                  :date       (date->ymd date)
                                  :start-time start-time
                                  :end-time   (add-minutes start-time duration-min)}
                           teacher-id (assoc :teacher-id teacher-id)
                           capacity   (assoc :capacity capacity))
                 method (if (= mode :edit) :patch :post)
                 path   (if (= mode :edit)
                          (str "/sessions/" session-id)
                          "/sessions")]
             (set-saving! true)
             (set-error! nil)
             (api/request!
              {:method     method
               :path       path
               :params     payload
               :on-success (fn [_]
                             (set-saving! false)
                             (when on-saved (on-saved)))
               :on-error   (fn [err]
                             (set-saving! false)
                             (set-error! (or (get-in err [:response :message])
                                             (str "Save failed ("
                                                  (or (:status err) "?") ")"))))})))
         [mode session-id branch-id program-id teacher-id
          date start-time duration-min capacity on-saved])]
    (cond
      loading?
      ($ ui/center {:style {:padding 32}} ($ ui/loader))

      :else
      ($ ui/stack {:gap "md"}
        (when error
          ($ ui/alert {:color "red" :variant "light" :title "Couldn't save"}
            error))

        ($ ui/select
          {:label       "Branch"
           :required    true
           :data        (clj->js branches)
           :value       branch-id
           :onChange    set-branch-id!
           :searchable  true})

        ($ ui/select
          {:label       "Program"
           :required    true
           :data        (clj->js (mapv (fn [p] {:value (:id p) :label (:name p)}) programs))
           :value       program-id
           :onChange    set-program-id!
           :searchable  true})

        ($ ui/select
          {:label       "Teacher"
           :data        (clj->js teachers)
           :value       teacher-id
           :onChange    set-teacher-id!
           :clearable   true
           :searchable  true
           :placeholder "Unassigned"})

        ($ DateInput
          {:label    "Date"
           :required true
           :value    date
           :onChange set-date!
           :valueFormat "ddd, MMM D, YYYY"})

        ;; Start time (grows) + a compact Duration cell: a narrow number-input
        ;; with the "Ends …" hint inline to its right, using the space a
        ;; full-width Duration field would otherwise waste.
        ($ ui/group {:grow true :align "flex-start"}
          ($ TimeInput
            {:label    "Start time"
             :required true
             :value    start-time
             :onChange #(set-start-time! (.. % -target -value))})
          ($ ui/input-wrapper {:label "Duration" :required true}
            ($ ui/group {:gap "xs" :align "center" :wrap "nowrap"}
              ($ ui/number-input
                {:required true
                 :value    duration-min
                 :min      15
                 :step     15
                 :suffix   " min"
                 :w        120
                 :onChange #(do (set-duration-touched! true)
                                (set-duration-min! %))})
              (when end-hint
                ($ ui/text {:size "sm" :c "dimmed" :style {:whiteSpace "nowrap"}}
                   (str "Ends " end-hint))))))

        ;; Soft flag: a session longer than the Program's default is allowed,
        ;; just surfaced so it reads as a deliberate choice.
        (when over-default?
          ($ ui/text {:size "sm" :c "orange"}
             (str "Longer than " (or program-name "the program") "’s default ("
                  program-default-min " min)")))

        ($ ui/number-input
          {:label       "Capacity"
           :description "Optional override of the program default."
           :value       capacity
           :min         0
           :onChange    set-capacity!
           :placeholder "Use program default"})

        ($ ui/group {:justify "flex-end" :gap "sm" :mt "md"}
          ($ ui/button {:variant "subtle" :onClick on-cancel :disabled saving?}
            "Cancel")
          ($ ui/button {:variant "filled" :color "brand"
                        :onClick on-save
                        :loading saving?
                        :disabled (or invalid?
                                      (not (and branch-id program-id date
                                                start-time duration-min)))}
            (if (= mode :edit) "Save" "Create")))))))
